import { useMemo } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { TablePaginationConfig } from "antd5/es/table";
import { FilterValue, SorterResult } from "antd5/es/table/interface";
import { startCase } from "lodash";
import isEqual from "lodash/isEqual";

import { AssignedToCell } from "components/table_components/AssignedToCell";
import { BuyerWithLogoCell } from "components/table_components/BuyerWithLogoCell";
import FrameworkLinkCell from "components/table_components/FrameworkLinkCell";
import { SupplierWithLogoCell } from "components/table_components/SupplierWithLogoCell";
import { TableCell } from "components/table_components/TableCellStyles";
import { CompanyUserCombobox } from "components/ui/form_components/CompanyUserCombobox";
import { BidStageSelect } from "components/ui/form_components/StageSelect/BidStageSelect";
import toast from "components/ui/toast/Toast";
import { appRoutes } from "lib/appRoutes";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import { BidData } from "lib/hooks/api/bid/bids/useBids";
import { useUpdateBids } from "lib/hooks/api/bid/bids/useUpdateBids";
import { PaginationState } from "lib/search/types";
import { Table } from "../../lib/core_components/Table";
import { ColumnType, commonTableColumns } from "../../lib/core_components/Table/ColumnTypes";
import { tableRowSelection } from "../../lib/core_components/Table/Table";
import { EventNames, useTracking } from "../../lib/tracking";
import { BidSortBy, BidSortOrder } from "../../lib/types/graphQLEnums";
import { formatDate } from "../../lib/utils";
import { BidColumns, BidSortState } from "./utils";

function getSortOrder(sort: BidSortState | undefined, key: BidSortBy): "ascend" | "descend" | null {
  if (!sort || sort.field !== key) {
    return null;
  }
  return sort.order === BidSortOrder.Asc ? "ascend" : "descend";
}

// --- Inline editing cells ---

function InlineStageCell({ bid }: { bid: BidData }) {
  const { mutate } = useUpdateBids({
    onSuccess: () => toast.success("Stage updated successfully"),
    onError: () => toast.error("Failed to update stage"),
  });

  return (
    <BidStageSelect
      value={bid.stage.id}
      truncate
      onValueChange={(stageId) =>
        mutate({ updateBidsRequest: { ids: [bid.id], updateData: { stageId } } })
      }
    />
  );
}

function InlineAssigneeCell({ bid }: { bid: BidData }) {
  const { mutate } = useUpdateBids({
    onSuccess: () => toast.success("Assignee updated successfully"),
    onError: () => toast.error("Failed to update assignee"),
  });

  return (
    <CompanyUserCombobox
      value={bid.assignedTo?.guid}
      truncate
      onChange={(assignedToId) =>
        mutate({ updateBidsRequest: { ids: [bid.id], updateData: { assignedToId } } })
      }
    />
  );
}

function getBidColumns({
  sort,
  columnSettings,
  handleBidRouteChange,
}: {
  sort: BidSortState | undefined;
  columnSettings: BidColumns[];
  handleBidRouteChange: (id: string) => void;
}): ColumnType<BidData>[] {
  const name: ColumnType<BidData> = {
    key: BidSortBy.Name,
    title: "Bid name",
    ellipsis: true,
    width: 390,
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.Name),
    render: (_, bid) => (
      <TableCell>
        <EllipsisTooltipRouterLink
          fullText={bid.name ?? "--"}
          linkText={bid.name ?? "--"}
          linkProps={{
            ...appRoutes.bidDetail(bid.id),
            onClick: (e) => {
              e.preventDefault();
              handleBidRouteChange(bid.id);
            },
          }}
        />
      </TableCell>
    ),
  };
  const buyer: ColumnType<BidData> = {
    key: BidSortBy.BuyerName,
    title: "Buyer",
    ellipsis: true,
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.BuyerName),
    render: (_, bid) => {
      const stotlesBuyers = bid.buyers.map((b) => ({ ...b, isCustom: false }));
      const customBuyers = bid.customBuyers.map((b) => ({ ...b, isCustom: true }));
      return BuyerWithLogoCell({ buyers: [...stotlesBuyers, ...customBuyers] });
    },
  };

  const stage: ColumnType<BidData> = {
    key: BidSortBy.StageName,
    title: "Stage",
    render: (_, bid) => (
      <TableCell minWidth={160}>
        <InlineStageCell bid={bid} />
      </TableCell>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.StageName),
    width: 160,
  };

  const assignedTo: ColumnType<BidData> = {
    key: BidSortBy.AssignedTo,
    title: "Assigned to",
    render: (_, bid) => (
      <TableCell minWidth={180} overflow="hidden">
        <InlineAssigneeCell bid={bid} />
      </TableCell>
    ),
    ellipsis: { showTitle: false },
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.AssignedTo),
    width: 180,
  };

  const createdBy: ColumnType<BidData> = {
    key: BidSortBy.CreatedBy,
    title: "Created by",
    render: (_, bid) => <AssignedToCell assignedTo={bid.createdBy} minWidth={180} />,
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.CreatedBy),
    width: 180,
  };

  const value: ColumnType<BidData> = {
    ...commonTableColumns.valueColumn,
    key: BidSortBy.Value,
    title: "Value",
    render: (_, bid) => (bid.value ? `£${bid.value.toLocaleString()}` : "--"),
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.Value),
  };

  const updatedAt: ColumnType<BidData> = {
    key: BidSortBy.UpdatedAt,
    title: "Last updated",
    render: (_, bid) => (
      <TableCell>{bid.updatedAt ? formatDate(new Date(bid.updatedAt), "en-GB") : "--"}</TableCell>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.UpdatedAt),
  };

  const submissionDate: ColumnType<BidData> = {
    key: BidSortBy.SubmissionDate,
    title: "Submission date",
    render: (_, bid) => (
      <TableCell>
        {bid.submissionDate ? formatDate(new Date(bid.submissionDate), "en-GB") : "--"}
      </TableCell>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.SubmissionDate),
  };

  const awardDate: ColumnType<BidData> = {
    key: BidSortBy.AwardDate,
    title: "Award date",
    render: (_, bid) => (
      <TableCell>{bid.awardDate ? formatDate(new Date(bid.awardDate), "en-GB") : "--"}</TableCell>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, BidSortBy.AwardDate),
  };

  const supplier: ColumnType<BidData> = {
    key: "supplier",
    title: "Relevant suppliers",
    ellipsis: true,

    render: (_, bid) => (
      <SupplierWithLogoCell
        suppliers={bid.suppliers.map((s) => ({
          id: s.id,
          name: s.name,
          logoUrl: s.logoUrl,
        }))}
        recordName={bid.name}
      />
    ),
  };

  const framework: ColumnType<BidData> = {
    key: "framework",
    title: "Framework",
    render: (_, bid) => <FrameworkLinkCell frameworks={bid.framework ? [bid.framework] : []} />,
  };

  const columns = {
    name,
    buyer,
    stage,
    assignedTo,
    createdBy,
    value,
    updatedAt,
    submissionDate,
    awardDate,
    supplier,
    framework,
  };

  return columnSettings.map((c) => columns[c]);
}
export type BidTableProps = {
  bids: BidData[] | undefined;
  total: number;
  isLoading: boolean;
  isError: boolean;
  columnSettings: BidColumns[];
  sort: BidSortState;
  onSortChange: (sort: BidSortState) => void;
  selectedRowKeys: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  onPaginationStateChange: (newState: PaginationState) => void;
  paginationState: PaginationState;
};

export function BidsTable({
  bids,
  total,
  isLoading,
  isError,
  sort,
  columnSettings,
  onSortChange,
  selectedRowKeys,
  onSelectionChange,
  paginationState,
  onPaginationStateChange,
}: BidTableProps) {
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const { logEvent } = useTracking();

  const columns = useMemo(() => {
    const handleBidRouteChange = (id: string) => {
      void navigate({
        to: `/bids/${id}`,
        state: (prev) => ({
          ...prev,
          from: location.pathname,
          previousSearchParams: location.searchStr,
        }),
      });
    };

    return getBidColumns({
      sort,
      columnSettings,
      handleBidRouteChange,
    });
  }, [sort, columnSettings, navigate, location]);

  const handleTableChange = (
    _: TablePaginationConfig,
    __: Record<string, FilterValue | null>,
    sorter: SorterResult<BidData> | SorterResult<BidData>[],
  ): void => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!sort && !currentSorter.order) {
      return;
    }

    const newSortState: BidSortState = {
      order: currentSorter.order === "descend" ? BidSortOrder.Desc : BidSortOrder.Asc,
      field: currentSorter.columnKey as BidSortBy,
    };

    if (!isEqual(sort, newSortState)) {
      onSortChange?.(newSortState);
      logEvent(EventNames.tableSorted, {
        Context: "Column action",
        "Sorted by": startCase(newSortState.field),
        "Sort order": newSortState.order,
      });
    }
  };

  return (
    <Table
      ariaLabel="Bids table"
      dataSource={bids}
      columns={columns}
      loading={isLoading}
      isError={isError}
      rowKey="id"
      rowSelection={tableRowSelection(
        selectedRowKeys,
        (selectedKeys) => onSelectionChange?.(selectedKeys),
        bids ?? [],
        "id",
      )}
      onChange={handleTableChange}
      scroll={{ x: "100%" }}
      pagination={{
        ...paginationState,
        total: total || 0,
        showSizeChanger: true,
        onChange: (page, pageSize) => {
          onPaginationStateChange({ current: page, pageSize });
        },
      }}
    />
  );
}
